import {
  ActionBase,
  PiecePropertyMap,
  PropertyType,
  TriggerBase,
  PieceMetadata,
} from '@activepieces/pieces-framework';
import {
  Action,
  ActionType,
  BranchOperator,
  CodeAction,
  PieceAction,
  PieceTrigger,
  Trigger,
  TriggerType,
  deepMergeAndCast,
  FlowVersion,
  PieceCategory,
  BranchExecutionType,
  RouterExecutionType,
  spreadIfDefined,
  isNil,
  Platform,
  flowStructureUtil,
  PieceType,
  PackageType,
  SWAGGER_PIECE_NAME_PREFIX,
} from '@activepieces/shared';
import { useRef } from 'react';
import semver from 'semver';

import { formUtils } from '../piece-properties/form-utils';

import {
  PieceSelectorItem,
  PieceStepMetadata,
  StepMetadata,
  StepMetadataWithSuggestions,
} from '@/features/pieces/lib/types';

export type PieceGroup = {
  title: string;
  pieces: StepMetadataWithSuggestions[];
};

export type VirtualizedItem = {
  id: string;
  displayName: string;
  height: number;
  isCategory: boolean;
} & (
  | { isCategory: true }
  | (StepMetadataWithSuggestions & { isCategory: false })
);

const PIECE_SELECTOR_ELEMENTS_HEIGHTS = {
  MAX_PIECE_SELECTOR_LIST_HEIGHT: 300,
  MIN_PIECE_SELECTOR_LIST_HEIGHT: 100,
  SEARCH_INPUT_DIV_HEIGHT: 113,
  PIECE_ITEM_HEIGHT: 49,
  ACTION_OR_TRIGGER_ITEM_HEIGHT: 49,
  CATEGORY_ITEM_HEIGHT: 28,
  ABOVE_LIST_SECTION_HEIGHT: 86,
} as const;

const defaultCode = `export const code = async (inputs) => {
  return true;
};`;

function toKey(stepMetadata: StepMetadata): string {
  switch (stepMetadata.type) {
    case ActionType.PIECE:
    case TriggerType.PIECE: {
      const pieceMetadata: PieceStepMetadata =
        stepMetadata as PieceStepMetadata;
      return `${stepMetadata.type}-${pieceMetadata.pieceName}-${pieceMetadata.pieceVersion}`;
    }
    default:
      return stepMetadata.type.toLowerCase();
  }
}

const isCorePiece = (piece: StepMetadata) =>
  piece.type !== TriggerType.PIECE && piece.type !== ActionType.PIECE
    ? true
    : (piece as PieceStepMetadata).categories.includes(PieceCategory.CORE);

const getStepName = (piece: StepMetadata, flowVersion: FlowVersion) => {
  if (piece.type === TriggerType.PIECE) {
    return 'trigger';
  }
  return flowStructureUtil.findUnusedName(flowVersion, 'step');
};

const isAiPiece = (piece: StepMetadata) =>
  piece.type === TriggerType.PIECE || piece.type === ActionType.PIECE
    ? (piece as PieceStepMetadata).categories.includes(
        PieceCategory.ARTIFICIAL_INTELLIGENCE
      )
    : false;

const isAppPiece = (piece: StepMetadata) =>
  !isAiPiece(piece) && !isCorePiece(piece);

const isActionOrTrigger = (
  item: PieceSelectorItem,
  stepMetadata: StepMetadata
): item is ActionBase | TriggerBase => {
  return [ActionType.PIECE, TriggerType.PIECE].includes(stepMetadata.type);
};

const isPieceStepMetadata = (
  stepMetadata: StepMetadata
): stepMetadata is PieceStepMetadata => {
  return [ActionType.PIECE, TriggerType.PIECE].includes(stepMetadata.type);
};

const popularPiecesPriority: Record<string, number> = {
  '@activepieces/piece-kizeo-forms': 1,
};

const isPopularPieces = (
  stepMetadata: StepMetadataWithSuggestions,
  platform: Platform
) => {
  if (
    stepMetadata.type !== TriggerType.PIECE &&
    stepMetadata.type !== ActionType.PIECE
  ) {
    return false;
  }
  const popularPieces = [
    '@activepieces/piece-google-sheets',
    '@activepieces/piece-google-calendar',
    '@activepieces/piece-google-drive',
    '@activepieces/piece-gmail',
    '@activepieces/piece-microsoft-excel-365',
    '@activepieces/piece-microsoft-onedrive',
    '@activepieces/piece-microsoft-outlook-calendar',
    '@activepieces/piece-slack',
    '@activepieces/piece-microsoft-teams',
    '@activepieces/piece-hubspot',
    '@activepieces/piece-salesforce',
    '@activepieces/piece-notion',
    '@activepieces/piece-openai',
    '@activepieces/piece-mailchimp',
    '@activepieces/piece-stripe',
    '@activepieces/piece-subflows',
    '@activepieces/piece-forms',
    '@activepieces/piece-kizeo-forms',
  ];
  const pinnedPieces = platform.pinnedPieces ?? [];
  return [...popularPieces, ...pinnedPieces].includes(
    (stepMetadata as PieceStepMetadata).pieceName
  );
};

const getPopularPiecePriority = (pieceName: string): number => {
  return popularPiecesPriority[pieceName] ?? 999;
};

const isFlowController = (stepMetadata: StepMetadata) => {
  if (stepMetadata.type === ActionType.PIECE) {
    return (stepMetadata as PieceStepMetadata).categories.includes(
      PieceCategory.FLOW_CONTROL
    );
  }
  return [ActionType.LOOP_ON_ITEMS, ActionType.ROUTER].includes(
    stepMetadata.type as ActionType
  );
};

const isUniversalAiPiece = (stepMetadata: StepMetadata) => {
  if (stepMetadata.type === ActionType.PIECE) {
    return (stepMetadata as PieceStepMetadata).categories.includes(
      PieceCategory.UNIVERSAL_AI
    );
  }
  return false;
};

const isUtilityCorePiece = (stepMetadata: StepMetadata, platform: Platform) => {
  if (stepMetadata.type === ActionType.CODE) {
    return true;
  }
  if (!isCorePiece(stepMetadata)) {
    return false;
  }
  return (
    !isFlowController(stepMetadata) && !isPopularPieces(stepMetadata, platform)
  );
};

const getDefaultStep = ({
  stepName,
  stepMetadata,
  actionOrTrigger,
  flowVersion,
  selectedStepName,
}: {
  stepName: string;
  stepMetadata: StepMetadata;
  actionOrTrigger: PieceSelectorItem;
  flowVersion: FlowVersion;
  selectedStepName?: string;
}): Action | Trigger => {
  const errorHandlingOptions = {
    continueOnFailure: {
      hide: true,
      value: false,
    },
    retryOnFailure: {
      hide: true,
      value: false,
    },
  };
  const defaultExecutionOptions = {
    executeOnce: {
      hide: true,
      value: false,
    },
  };
  const isPieceStep =
    isActionOrTrigger(actionOrTrigger, stepMetadata) &&
    isPieceStepMetadata(stepMetadata);
  const input = isPieceStep
    ? formUtils.getDefaultValueForStep(
        actionOrTrigger.requireAuth
          ? {
              ...spreadIfDefined('auth', stepMetadata.auth),
              ...actionOrTrigger.props,
            }
          : actionOrTrigger.props,
        {}
      )
    : {};

  const selectedStep = selectedStepName
    ? flowStructureUtil.getStepOrThrow(selectedStepName, flowVersion)
    : undefined;

  const common = {
    name: stepName,
    valid: isPieceStep
      ? checkPieceInputValidity(input, actionOrTrigger.props) &&
        (actionOrTrigger.requireAuth && !isNil(actionOrTrigger.props['auth'])
          ? !isNil(input['auth'])
          : true)
      : stepMetadata.type === ActionType.CODE
      ? true
      : false,
    displayName: actionOrTrigger.displayName,
    skip: false,
    settings: {
      inputUiInfo: {
        customizedInputs: {},
      },
      position: selectedStep?.settings.position ?? { x: 0, y: 0 },
    },
  };

  switch (stepMetadata.type) {
    case ActionType.CODE:
      return deepMergeAndCast<CodeAction>(
        {
          type: ActionType.CODE,
          settings: {
            sourceCode: {
              code: defaultCode,
              packageJson: '{}',
            },
            input,
            inputUiInfo: {
              customizedInputs: {},
            },
            errorHandlingOptions: errorHandlingOptions,
            executionOptions: defaultExecutionOptions,
          },
        },
        common
      );
    case ActionType.LOOP_ON_ITEMS:
      return deepMergeAndCast<Action>(
        {
          type: ActionType.LOOP_ON_ITEMS,
          settings: {
            items: '',
            inputUiInfo: {
              customizedInputs: {},
            },
          },
        },
        common
      );
    case ActionType.ROUTER:
      return deepMergeAndCast<Action>(
        {
          type: ActionType.ROUTER,
          settings: {
            executionType: RouterExecutionType.EXECUTE_FIRST_MATCH,
            branches: [
              {
                conditions: [
                  [
                    {
                      operator: BranchOperator.TEXT_EXACTLY_MATCHES,
                      firstValue: '',
                      secondValue: '',
                      caseSensitive: false,
                    },
                  ],
                ],
                branchType: BranchExecutionType.CONDITION,
                branchName: 'Branch 1',
              },
              {
                branchType: BranchExecutionType.FALLBACK,
                branchName: 'Otherwise',
              },
            ],
            inputUiInfo: {
              customizedInputs: {},
            },
          },
          children: [null, null],
        },
        common
      );
    case ActionType.PIECE: {
      return deepMergeAndCast<PieceAction>(
        {
          type: ActionType.PIECE,
          settings: {
            pieceName: stepMetadata.pieceName,
            pieceType: stepMetadata.pieceType,
            packageType: stepMetadata.packageType,
            actionName: actionOrTrigger.name,
            pieceVersion: stepMetadata.pieceVersion,
            input,
            errorHandlingOptions: errorHandlingOptions,
            executionOptions: defaultExecutionOptions,
          },
        },
        common
      );
    }
    case TriggerType.PIECE: {
      return deepMergeAndCast<PieceTrigger>(
        {
          type: TriggerType.PIECE,
          settings: {
            pieceName: stepMetadata.pieceName,
            pieceType: stepMetadata.pieceType,
            packageType: stepMetadata.packageType,
            triggerName: actionOrTrigger.name,
            pieceVersion: stepMetadata.pieceVersion,
            input,
          },
        },
        common
      );
    }
    default:
      throw new Error('Unsupported type: ' + stepMetadata.type);
  }
};

const checkPieceInputValidity = (
  input: Record<string, unknown>,
  props: PiecePropertyMap
) => {
  return Object.entries(props).reduce((acc, [key, property]) => {
    if (
      property.required &&
      property.type !== PropertyType.DYNAMIC &&
      isNil(input[key])
    ) {
      return false;
    }
    return acc;
  }, true);
};

const useAdjustPieceListHeightToAvailableSpace = (
  isPieceSelectorOpen: boolean
) => {
  const {
    MAX_PIECE_SELECTOR_LIST_HEIGHT,
    MIN_PIECE_SELECTOR_LIST_HEIGHT,
    ABOVE_LIST_SECTION_HEIGHT,
  } = PIECE_SELECTOR_ELEMENTS_HEIGHTS;

  const listHeightRef = useRef<number>(MAX_PIECE_SELECTOR_LIST_HEIGHT);
  const popoverTriggerRef = useRef<HTMLButtonElement | null>(null);
  if (
    isPieceSelectorOpen &&
    popoverTriggerRef.current
  ) {
    const popoverTriggerRect =
      popoverTriggerRef.current.getBoundingClientRect();
    const popOverFullHeight =
      MAX_PIECE_SELECTOR_LIST_HEIGHT + ABOVE_LIST_SECTION_HEIGHT;
    const isRenderingPopoverBelowTrigger =
      popoverTriggerRect.top <
      (window.innerHeight || document.documentElement.clientHeight) -
        popoverTriggerRect.bottom;
    if (isRenderingPopoverBelowTrigger) {
      const isPopoverOverflowing =
        popoverTriggerRect.bottom + popOverFullHeight >
        (window.innerHeight || document.documentElement.clientHeight);
      if (isPopoverOverflowing) {
        listHeightRef.current = Math.max(
          MIN_PIECE_SELECTOR_LIST_HEIGHT,
          MAX_PIECE_SELECTOR_LIST_HEIGHT +
            (window.innerHeight || document.documentElement.clientHeight) -
            popOverFullHeight -
            popoverTriggerRect.bottom
        );
      }
    } else {
      const isPopoverOverflowing =
        popoverTriggerRect.top - popOverFullHeight < 0;
      if (isPopoverOverflowing) {
        listHeightRef.current = Math.max(
          MIN_PIECE_SELECTOR_LIST_HEIGHT,
          MAX_PIECE_SELECTOR_LIST_HEIGHT -
            Math.abs(popoverTriggerRect.top - popOverFullHeight)
        );
      }
    }
  }
  return {
    listHeightRef,
    popoverTriggerRef,
    maxListHeight: MAX_PIECE_SELECTOR_LIST_HEIGHT,
    aboveListSectionHeight: ABOVE_LIST_SECTION_HEIGHT,
  };
};

const useIsMobile = () => {
  return (window.innerWidth || document.documentElement.clientWidth) < 768;
};

const isSwaggerPiece = (piece: StepMetadata): boolean => {
  if (piece.type !== ActionType.PIECE) {
    return false;
  }
  const pieceMetadata = piece as PieceStepMetadata;
  return (
    pieceMetadata.pieceName.startsWith(SWAGGER_PIECE_NAME_PREFIX) ||
    pieceMetadata.categories.includes(PieceCategory.SWAGGER)
  );
};

export const transformSwaggerPiece = (
  swaggerPiece: PieceMetadata
): PieceStepMetadata => {
  return {
    type: ActionType.PIECE,
    pieceName: swaggerPiece.name,
    pieceVersion: swaggerPiece.version,
    pieceType: PieceType.OFFICIAL,
    packageType: PackageType.REGISTRY,
    auth: swaggerPiece.auth,
    categories: [...(swaggerPiece.categories || []), PieceCategory.SWAGGER],
    displayName: swaggerPiece.displayName,
    description: swaggerPiece.description,
    logoUrl: swaggerPiece.logoUrl,
  };
};

const isOldVersion = (
  piece: PieceStepMetadata,
  allPieces: PieceStepMetadata[]
): boolean => {
  const piecesWithSameName = allPieces.filter(
    (p) => p.pieceName === piece.pieceName
  );
  if (piecesWithSameName.length <= 1) return false;

  const latestVersion = piecesWithSameName.reduce((latest, current) => {
    return semver.gt(current.pieceVersion, latest.pieceVersion)
      ? current
      : latest;
  });

  return piece.pieceVersion !== latestVersion.pieceVersion;
};

const transformPiecesMetadataToVirtualizedItems = (
  pieceGroups: PieceGroup[],
  showActionsOrTriggersInsidePiecesList: boolean
): VirtualizedItem[] => {
  return pieceGroups.reduce<VirtualizedItem[]>((result, group) => {
    result.push({
      id: group.title,
      displayName: group.title,
      height: PIECE_SELECTOR_ELEMENTS_HEIGHTS.CATEGORY_ITEM_HEIGHT,
      isCategory: true,
    });

    group.pieces.forEach((pieceMetadata, index) => {
      result.push({
        ...pieceMetadata,
        id: `${pieceMetadata.displayName}-${index}`,
        height: getItemHeight(
          pieceMetadata,
          showActionsOrTriggersInsidePiecesList
        ),
        isCategory: false,
      });
    });

    return result;
  }, []);
};

const getItemHeight = (
  pieceMetadata: StepMetadataWithSuggestions,
  showActionsOrTriggersInsidePiecesList: boolean
): number => {
  const { ACTION_OR_TRIGGER_ITEM_HEIGHT, PIECE_ITEM_HEIGHT } =
    PIECE_SELECTOR_ELEMENTS_HEIGHTS;

  if (showActionsOrTriggersInsidePiecesList) {
    if (pieceMetadata.type === ActionType.PIECE) {
      const actionsListWithoutHiddenActions =
        pieceMetadata.suggestedActions || [];
      const actionsCount = actionsListWithoutHiddenActions.length;
      return ACTION_OR_TRIGGER_ITEM_HEIGHT * actionsCount + PIECE_ITEM_HEIGHT;
    }
    if (pieceMetadata.type === TriggerType.PIECE) {
      const triggersCount = Object.values(
        pieceMetadata.suggestedTriggers ?? {}
      ).length;
      return ACTION_OR_TRIGGER_ITEM_HEIGHT * triggersCount + PIECE_ITEM_HEIGHT;
    }

    const isCoreAction =
      pieceMetadata.type === ActionType.CODE ||
      pieceMetadata.type === ActionType.LOOP_ON_ITEMS ||
      pieceMetadata.type === ActionType.ROUTER;
    if (isCoreAction) {
      return ACTION_OR_TRIGGER_ITEM_HEIGHT + PIECE_ITEM_HEIGHT;
    }
  }

  return PIECE_ITEM_HEIGHT;
};

export const pieceSelectorUtils = {
  getDefaultStep,
  isCorePiece,
  getStepName,
  isAiPiece,
  isAppPiece,
  toKey,
  isPopularPieces,
  getPopularPiecePriority,
  isUtilityCorePiece,
  isFlowController,
  isUniversalAiPiece,
  useAdjustPieceListHeightToAvailableSpace,
  useIsMobile,
  isSwaggerPiece,
  transformSwaggerPiece,
  isOldVersion,
  transformPiecesMetadataToVirtualizedItems,
};
